import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../lib/app-context';
import { Icon, type IconName } from './Icon';
import { QUICK_REACTIONS } from './MessageExtras';

// Per-message actions, the way messengers do it: a small toolbar beside the bubble on hover (quick reaction,
// reply, more), a menu with everything else, and on touch screens a long press that opens the same choices
// as a bottom sheet. Nothing is drawn inside the bubble, so short messages stay short.

export interface MessageAction {
  key: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  onSelect: () => void;
}

export type ActionsOpen = { id: string; mode: 'menu' | 'react' } | null;

/** Delivery state as icons: clock while waiting, one tick sent, two delivered, two in colour read. */
export function DeliveryTicks({ status, label }: { status: string; label: string }) {
  let mark: ReactNode;
  if (status === 'queued' || status === 'sending') {
    mark = (
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8 4.8V8l2.2 1.4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  } else if (status === 'failed') {
    mark = (
      <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" fill="currentColor" />
        <path d="M8 4.5v4.2M8 11.2v.1" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  } else if (status === 'sent') {
    mark = (
      <svg viewBox="0 0 16 11" width="15" height="11" aria-hidden="true">
        <path d="M2 6l3.2 3.2L13.5 1.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  } else {
    mark = (
      <svg viewBox="0 0 20 11" width="18" height="11" aria-hidden="true">
        <path d="M1.5 6l3.2 3.2L13 1.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.6 8.8l.7.6L17.6 1.2" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <span className={`tick tick-${status}`} title={label}>
      {mark}
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/** Long press (touch) and right click open a message's actions; `bind(id)` gives the props for its bubble. */
export function useLongPress(open: (id: string) => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const clear = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  return (id: string) => ({
    onPointerDown: (e: ReactPointerEvent) => {
      if (e.pointerType === 'mouse') return;
      start.current = { x: e.clientX, y: e.clientY };
      clear();
      timer.current = setTimeout(() => {
        navigator.vibrate?.(12);
        open(id);
      }, 450);
    },
    onPointerMove: (e: ReactPointerEvent) => {
      if (start.current && Math.hypot(e.clientX - start.current.x, e.clientY - start.current.y) > 10) clear();
    },
    onPointerUp: clear,
    onPointerCancel: clear,
    onContextMenu: (e: ReactMouseEvent) => {
      // Links and selected text keep the browser's own menu.
      if ((e.target as HTMLElement).closest('a') || String(window.getSelection() ?? '').length) return;
      e.preventDefault();
      clear();
      open(id);
    },
  });
}

function useDismiss(active: boolean, refs: RefObject<HTMLElement | null>[], onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const down = (e: PointerEvent) => {
      if (!refs.some((r) => r.current?.contains(e.target as Node))) onClose();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the refs are stable
  }, [active, onClose]);
}

const isTouch = () => typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

export function MessageActions({
  open,
  setOpen,
  canReact,
  onReact,
  onReply,
  actions,
  mine,
}: {
  open: 'menu' | 'react' | null;
  setOpen: (mode: 'menu' | 'react' | null) => void;
  canReact: boolean;
  onReact: (emoji: string) => void;
  onReply: (() => void) | null;
  actions: MessageAction[];
  mine: boolean;
}) {
  const { i18n } = useApp();
  const { t } = i18n;
  const ref = useRef<HTMLDivElement>(null);
  const tools = useRef<HTMLDivElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const sheet = useRef<HTMLDivElement>(null);
  useDismiss(open !== null, [ref, pop, sheet], () => setOpen(null));

  // The popover is placed against the window: below the toolbar when it fits, else above, always on screen.
  useLayoutEffect(() => {
    if (!open || !tools.current || !pop.current) return setPos(null);
    const t = tools.current.getBoundingClientRect();
    const { offsetHeight: h, offsetWidth: w } = pop.current;
    const gap = 6;
    let top = t.bottom + gap;
    if (top + h > window.innerHeight - 8) top = t.top - gap - h >= 8 ? t.top - gap - h : Math.max(8, window.innerHeight - 8 - h);
    const left = Math.min(Math.max(8, mine ? t.right - w : t.left), window.innerWidth - w - 8);
    setPos({ top, left });
  }, [open, mine]);

  // Scrolling the conversation would leave the popover behind; close it instead.
  useEffect(() => {
    if (!open || isTouch()) return;
    const close = () => setOpen(null);
    const scroller = ref.current?.closest('.message-scroller');
    scroller?.addEventListener('scroll', close, { passive: true });
    window.addEventListener('resize', close);
    return () => {
      scroller?.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, [open, setOpen]);

  // Focus the first item for keyboard users.
  useEffect(() => {
    if (open) ref.current?.querySelector<HTMLElement>('.msg-menu [role="menuitem"], .msg-react-picker button')?.focus();
  }, [open]);

  const pick = (fn: () => void) => () => {
    setOpen(null);
    fn();
  };
  const reactions = canReact && (
    <div className="msg-react-picker" role="group" aria-label={t('chat.react')}>
      {QUICK_REACTIONS.map((e) => (
        <button key={e} type="button" onClick={pick(() => onReact(e))} aria-label={`${t('chat.react')} ${e}`}>
          {e}
        </button>
      ))}
    </div>
  );
  const menu = (
    <div className="msg-menu" role="menu" aria-label={t('chat.messageActions')}>
      {actions.map((a) => (
        <button key={a.key} type="button" role="menuitem" className={a.danger ? 'danger' : undefined} onClick={pick(a.onSelect)}>
          <Icon name={a.icon} size={18} /> <span>{a.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div ref={ref} className={`msg-tools-wrap ${mine ? 'mine' : 'theirs'}${open ? ' is-open' : ''}`}>
      <div ref={tools} className="msg-tools" role="group" aria-label={t('chat.messageActions')}>
        {canReact && (
          <button type="button" className="msg-tool" aria-label={t('chat.react')} aria-expanded={open === 'react'} onClick={() => setOpen(open === 'react' ? null : 'react')}>
            <Icon name="smile" size={18} />
          </button>
        )}
        {onReply && (
          <button type="button" className="msg-tool" aria-label={t('chat.reply')} onClick={pick(onReply)}>
            <Icon name="reply" size={18} />
          </button>
        )}
        <button type="button" className="msg-tool" aria-label={t('chat.moreActions')} aria-haspopup="menu" aria-expanded={open === 'menu'} onClick={() => setOpen(open === 'menu' ? null : 'menu')}>
          <Icon name="more" size={18} />
        </button>
      </div>
      {open && isTouch() ? (
        // Touch: one bottom sheet with reactions and every action (on the page root, above every bar).
        createPortal(
        <>
          <div className="msg-sheet-backdrop" aria-hidden onPointerDown={() => setOpen(null)} />
          <div ref={sheet} className="msg-sheet">
            {reactions}
            {onReply && (
              <div className="msg-menu" role="menu">
                <button type="button" role="menuitem" onClick={pick(onReply)}>
                  <Icon name="reply" size={18} /> <span>{t('chat.reply')}</span>
                </button>
              </div>
            )}
            {menu}
          </div>
        </>,
        document.body,
        )
      ) : (
        open &&
        createPortal(
          <div ref={pop} className="msg-popover" style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden' }}>
            {open === 'react' ? reactions : menu}
          </div>,
          document.body,
        )
      )}
    </div>
  );
}
